import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ModelViewer from './components/ModelViewer/ModelViewer';
import VibrationDashboard from './components/Annotations/VibrationDashboard';
import TemperatureDashboard from './components/Annotations/TemperatureDashboard';
import { api } from './api.js';

// --- CONSTANTES ---
const MODO_TESTING = false;
const REFRESH_MS = 30_000;

// Función helper para buscar propiedades en el historial de sensores
function pickProp(sensor, predicate) {
  if (!sensor || !Array.isArray(sensor.lastData)) return null;
  return sensor.lastData.find(predicate) || sensor.lastData[0] || null;
}

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
function App() {
  // --- 1. ESTADOS DE LA UI 3D ---
  const [selectedPath, setSelectedPath] = useState("/assets/modelos3d/bombeo_lq.splat"); // Asume baja por defecto
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isQualityMenuOpen, setIsQualityMenuOpen] = useState(false);
  const [isUIReady, setIsUIReady] = useState(false);

  // --- 2. ESTADOS DEL BACKEND (IoT) ---
  const [sensors, setSensors] = useState([]);
  const [history, setHistory] = useState([null, null, null, null]);

  // --- 3. LÓGICA DE CARGA DE DATOS ---
  const loadSensors = useCallback(async () => {
    try {
      const r = await api.sensors();
      const list = r.data || [];
      setSensors(list);
      const hs = await Promise.all(list.map(s =>
        s.found ? api.history(s.identificativo, 1).then(res => res.data).catch(() => null) : Promise.resolve(null)
      ));
      setHistory(hs);
    } catch (e) {
      console.error(`No se pudo cargar sensores: ${e.message}`);
    }
  }, []);

  useEffect(() => { 
    loadSensors(); 
    const iv = setInterval(loadSensors, REFRESH_MS);
    return () => clearInterval(iv);
  }, [loadSensors]);

  // --- 4. PROCESAMIENTO MATEMÁTICO DE DATOS ---
  const b1Vib = useMemo(() => {
    const last = pickProp(sensors[1], d => /velocidad|rms/i.test(d.propiedad)) || pickProp(sensors[1], () => true);
    return last ? { value: Number(last.valorcalculado ?? last.valorrecibido) } : null;
  }, [sensors]);

  const vibStats = useMemo(() => {
    const series = history?.[1]?.find?.(p => /velocidad|rms/i.test(p.nombre))?.valores || history?.[1]?.[0]?.valores || [];
    if (!series.length) return { max: null, mean: null };
    let max = -Infinity, sum = 0;
    for (const v of series) {
      const x = Number(v.valorcalculado ?? v.valorrecibido);
      if (x > max) max = x;
      sum += x;
    }
    return { max, mean: sum / series.length };
  }, [history]);

  const b2Vib = useMemo(() => {
    const last = pickProp(sensors[3], d => /velocidad|rms/i.test(d.propiedad)) || pickProp(sensors[3], () => true);
    return last ? { value: Number(last.valorcalculado ?? last.valorrecibido) } : null;
  }, [sensors]);

  const vib2Stats = useMemo(() => {
    const series = history?.[3]?.find?.(p => /velocidad|rms/i.test(p.nombre))?.valores || history?.[3]?.[0]?.valores || [];
    if (!series.length) return { max: null, mean: null };
    let max = -Infinity, sum = 0;
    for (const v of series) {
      const x = Number(v.valorcalculado ?? v.valorrecibido);
      if (x > max) max = x;
      sum += x;
    }
    return { max, mean: sum / series.length };
  }, [history]);

  const tempSeries = useMemo(() => {
    const h1 = history?.[0]?.find?.(p => /temperatura/i.test(p.nombre))?.valores || history?.[0]?.[0]?.valores || [];
    const h2 = history?.[2]?.find?.(p => /temperatura/i.test(p.nombre))?.valores || history?.[2]?.[0]?.valores || [];
    const len = Math.max(h1.length, h2.length);
    if (!len) return [];
    const step = Math.max(1, Math.floor(len / 60));
    const out = [];
    for (let i = 0; i < len; i += step) {
      const a = h1[i]; const b = h2[i];
      out.push({
        t: (a?.fecha || b?.fecha || '').slice(11, 16),
        b1: a ? Number(a.valorcalculado ?? a.valorrecibido) : null,
        b2: b ? Number(b.valorcalculado ?? b.valorrecibido) : null
      });
    }
    return out;
  }, [history]);

  // --- 5. JSX de cada anotación, memoizado por separado con dependencias mínimas ---
  const b1VibContent = useMemo(() => (
    <VibrationDashboard value={b1Vib?.value} max24h={vibStats.max} mean24h={vibStats.mean} />
  ), [b1Vib, vibStats]);

  const b1TempContent = useMemo(() => (
    <TemperatureDashboard series={tempSeries} bomba={1} />
  ), [tempSeries]);

  const b2VibContent = useMemo(() => (
    <VibrationDashboard value={b2Vib?.value} max24h={vib2Stats.max} mean24h={vib2Stats.mean} />
  ), [b2Vib, vib2Stats]);

  const b2TempContent = useMemo(() => (
    <TemperatureDashboard series={tempSeries} bomba={2} />
  ), [tempSeries]);

  // --- 6. CONFIGURACIÓN DEL ENTORNO 3D (estructura estática) ---
  const datosBombeo = useMemo(() => {
    return {
      id: 'bombeo',
      modelPaths: {
        alta: "/assets/modelos3d/bombeo_hq.splat",
        media: "/assets/modelos3d/bombeo_mq.splat",
        baja: "/assets/modelos3d/bombeo_lq.splat"
      },
      collisionPath: "/assets/modelos3d/bombeo_collision.obj",
      collisionRotation: [0, 0, 0],
      initialCamera: [1, 0, -1],
      initialLookAt: [0, 0, 0],
      rotation: [Math.PI / 2, 0, 0],
      position: [0, 0, 0],
      maps: [
        {
          name: "General",
          image: "/assets/images/mapa-bombeo.png",
          points: [
            { label: "Entrada", top: 16, left: 57, camPosition: [1, 0, -1], camLookAt: [0, 0, 0] },
            { label: "Panel control", top: 16, left: 47, camPosition: [-2, 0, -1], camLookAt: [-2, 0, -2] },
            { label: "Cajas electricas", top: 42, left: 28, camPosition: [-4, 0, 1.5], camLookAt: [-10, -2, -10] },
            { label: "Valvulas", top: 54, left: 54, camPosition: [-1, 0, 2], camLookAt: [100, 0, 1.5] },
            { label: "Salida tuberia", top: 87, left: 59, camPosition: [1, -0.4, 5], camLookAt: [1, 0, -1] },
          ],
          navigation: [
            {
              position: [2, -1.3, -1.5], rotationY: 90 * (Math.PI / 180),
              visibleFrom: "Entrada", targetLabel: "Salida tuberia", label: "Ir a Salida tuberia",
              path: [[3.6, 0, -1], [4, -0.2, 3]]
            },
            {
              position: [2, -1.8, 4.5], rotationY: 120 * (Math.PI / 180),
              visibleFrom: "Salida tuberia", targetLabel: "Entrada", label: "Ir a Entrada",
              path: [[4, -0.2, 3], [3.6, 0, -1]]
            },
            {
              position: [1, -1.3, 0], rotationY: 0 * (Math.PI / 180),
              visibleFrom: "Entrada", targetLabel: "Valvulas", label: "Ir a Valvulas",
              path: [[1, 0, 1]]
            },
            {
              position: [-0.6, -1.3, 1.6], rotationY: 120 * (Math.PI / 180),
              visibleFrom: "Valvulas", targetLabel: "Entrada", label: "Ir a Entrada",
              path: [[1, 0, 1]]
            },
            {
              position: [-0.5, -1.3, -1], rotationY: -90 * (Math.PI / 180),
              visibleFrom: "Entrada", targetLabel: "Panel control", label: "Ir a Panel control",
              path: []
            },
            {
              position: [-0.5, -1.3, -1], rotationY: 90 * (Math.PI / 180),
              visibleFrom: "Panel control", targetLabel: "Entrada", label: "Ir a Entrada",
              path: []
            },
            {
              position: [-3, -1.3, -1], rotationY: -90 * (Math.PI / 180),
              visibleFrom: "Panel control", targetLabel: "Cajas electricas", label: "Ir a Cajas electricas",
              path: [[-4, 0, -1]]
            },
            {
              position: [-4, -1.3, 0.5], rotationY: 180 * (Math.PI / 180),
              visibleFrom: "Cajas electricas", targetLabel: "Panel control", label: "Ir a Panel control",
              path: [[-4, 0, -1]]
            },
          ],
          annotations: [
            {
              title: "Bomba 1: Nivel de vibración", icon: "/assets/images/annotations/icons/ubi.png",
              content: b1VibContent,
              position: [-1.61, -0.76, -0.12], maxDistance: 50, maxXDistance: 20, maxYDistance: 20, maxZDistance: 20,
            },
            {
              title: "Bomba 1: Nivel de temperatura", icon: "/assets/images/annotations/icons/ubi.png",
              content: b1TempContent,
              position: [-1.73, -0.66, -0.57], maxDistance: 50, maxXDistance: 20, maxYDistance: 20, maxZDistance: 20,
            },
            {
              title: "Bomba 2: Nivel de vibración", icon: "/assets/images/annotations/icons/ubi.png",
              content: b2VibContent,
              position: [-4.75, -0.62, 0.47], maxDistance: 50, maxXDistance: 20, maxYDistance: 20, maxZDistance: 20,
            },
            {
              title: "Bomba 2: Nivel de temperatura", icon: "/assets/images/annotations/icons/ubi.png",
              content: b2TempContent,
              position: [-4.88, -0.71, 0.76], maxDistance: 50, maxXDistance: 20, maxYDistance: 20, maxZDistance: 20,
            },
          ]
        }
      ]
    };
  }, [b1VibContent, b1TempContent, b2VibContent, b2TempContent]);


  // --- INTERFAZ REACT ---
  const getNombreCalidad = (path) => {
    if (path === datosBombeo.modelPaths.alta) return "Ultra";
    if (path === datosBombeo.modelPaths.media) return "Alta";
    if (path === datosBombeo.modelPaths.baja) return "Standard";
    return "";
  };

  const handleChangeQuality = (newPath) => {
    setIsUIReady(false); 
    setSelectedPath(newPath);
    setIsQualityMenuOpen(false); 
  };

  return (
    <div style={{ 
      position: 'fixed', inset: 0, width: '100%', height: '100%',
      overflow: 'hidden', margin: 0, padding: 0, background: 'black',
      fontFamily: 'sans-serif', color: 'white'
    }}>
      
      {/* MENÚ DE CALIDAD FLOTANTE */}
      {selectedPath && (
        <nav style={{
          position: 'absolute', top: '20px', left: '20px', zIndex: 9999,
          background: 'rgba(0, 0, 0, 0.6)', padding: '6px 16px', borderRadius: '30px',
          backdropFilter: 'blur(5px)', border: '1px solid rgba(255, 255, 255, 0.15)',
          display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
          opacity: isUIReady ? 1 : 0, pointerEvents: isUIReady ? 'auto' : 'none', transition: 'opacity 0.5s ease-in'
        }}>
          
          <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
            Calidad: <span style={{ color: 'white' }}>{getNombreCalidad(selectedPath)}</span>
          </span>

          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                setIsQualityMenuOpen(!isQualityMenuOpen); 
              }}
              title="Cambiar calidad del modelo"
              style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: isQualityMenuOpen ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                color: 'white', border: '1px solid rgba(255, 255, 255, 0.4)',
                fontSize: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                cursor: 'pointer', transition: 'background 0.2s',
                transform: isQualityMenuOpen ? 'scale(1.1)' : 'scale(1)'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
              onMouseLeave={(e) => { if(!isQualityMenuOpen) e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            >
              ⚙️
            </button>

            {isQualityMenuOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                marginTop: '10px', background: 'rgba(0, 0, 0, 0.6)', padding: '8px',
                borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.15)',
                display: 'flex', flexDirection: 'column', gap: '8px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)',
                animation: 'fadeIn 0.2s ease-out'
              }}>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleChangeQuality(datosBombeo.modelPaths.alta); }}
                  style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#555', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}
                >Ultra</button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleChangeQuality(datosBombeo.modelPaths.media); }}
                  style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}
                >Alta</button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleChangeQuality(datosBombeo.modelPaths.baja); }}
                  style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#333', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}
                >Standard</button>
              </div>
            )}
          </div>
        </nav>
      )}

      {isQualityMenuOpen && (
        <div onClick={() => setIsQualityMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
      )}

      {/* --- BOTÓN DE AYUDA --- */}
      <button
        onClick={() => setIsHelpOpen(true)}
        style={{
          position: 'absolute', top: '20px', right: '20px', width: '40px', height: '40px',
          borderRadius: '50%', background: 'rgba(0, 0, 0, 0.6)', color: 'white',
          border: '1px solid rgba(255, 255, 255, 0.4)', fontSize: '20px', fontWeight: 'bold',
          cursor: 'pointer', zIndex: 10000, backdropFilter: 'blur(5px)', 
          opacity: isUIReady ? 1 : 0, pointerEvents: isUIReady ? 'auto' : 'none', transition: 'background 0.2s, opacity 0.5s ease-in'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.8)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.6)'; }}
      >
        ?
      </button>

      {/* --- POPUP DE CONTROLES --- */}
      {isHelpOpen && (
        <>
          <div onClick={() => setIsHelpOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10000, cursor: 'pointer' }} />
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.6)', border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '12px', padding: '25px', zIndex: 10001, width: '320px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)', pointerEvents: 'auto'
          }}>
            <button 
              onClick={() => setIsHelpOpen(false)}
              style={{ position: 'absolute', top: '10px', right: '15px', background: 'none', border: 'none', color: '#aaa', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'white'} onMouseLeave={(e) => e.currentTarget.style.color = '#aaa'}
            >✖</button>
            <h3 style={{ marginTop: 0, color: 'white', marginBottom: '15px', fontSize: '18px' }}>Controles</h3>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.8', margin: 0, fontSize: '15px' }}>
              {datosBombeo.orbitMode ? (
                <>
                  <li><b>Click Izq + Arrastrar:</b> Rotar modelo 3D</li>
                  <li><b>Click Der + Arrastrar:</b> Mover cámara</li>
                  <li><b>Rueda ratón:</b> Zoom</li>
                </>
              ) : (
                <>
                  <li><b>Click Izq + Arrastrar:</b> Rotar cámara</li>
                  <li><b>Click en 'i' o Flecha:</b> Moverse de localización</li>
                  <li><b>Click en icono:</b> Leer anotación</li>
                </>
              )}
            </ul>
          </div>
        </>
      )}

      {/* key={selectedPath}: al cambiar de calidad se remonta el visor entero (posición, cámara, estado) */}
      {selectedPath && <ModelViewer key={selectedPath} {...datosBombeo} modelPath={selectedPath} onLoaded={() => setIsUIReady(true)} testingMode={MODO_TESTING} />}
      
      {MODO_TESTING && (
        <div style={{ position: 'absolute', bottom: '20px', right: '20px', color: 'red', fontWeight: 'bold', zIndex: 10000, pointerEvents: 'none' }}>
          ⚠️ MODO DESARROLLADOR ACTIVADO
        </div>
      )}
    </div>
  );
}

export default App;